const moment = require('moment-timezone');

function getMaxDaysInMonth(month) {
  if (month < 1 || month > 12) {
    throw new Error(`Month ${month} is not a valid month.`);
  }

  let daysInMonth;
  switch (month) {
    case 2:
      daysInMonth = 29;
      break;
    case 4:
    case 6:
    case 9:
    case 11:
      daysInMonth = 30;
      break;
    default:
      daysInMonth = 31;
      break;
  }

  return daysInMonth;
}

function getDaysInMonth(year, month) {
  if (month === 2) {
    return moment({ year }).isLeapYear() ? 29 : 28;
  }

  return getMaxDaysInMonth(month);
}

function assertMonthAndDayAreValid(month, day) {
  if (month < 1 || month > 12) {
    throw new Error(`Month ${month} is not a valid month.`);
  }
  const daysInMonth = getMaxDaysInMonth(month);
  if (day < 1 || day > daysInMonth) {
    throw new Error(`Day ${day} is not a valid day for month ${month}.`);
  }
}

function assertDayIsValid(day) {
  if (day < 1 || day > 31) {
    throw new Error(`Day ${day} is not a valid day for any month.`);
  }
}

function getNextQuarterlyDate(currentDate, recurringMonth, recurringDay) {
  assertMonthAndDayAreValid(recurringMonth, recurringDay);

  // create last 8 starting in previous year on the recurring month / day.
  // note that there may be other algorithms, but this one is fairly readable(??).
  // with the "calculate a bunch of quarterly due dates and see which is after the current date" algorithm,
  // 8 will guarantee the next one is there.
  // current date is 2017 Jan 1 and recurrence starts Dec 31
  //  -> *2017 Mar 31*, 2017 Jun 31, 2017 Sep 31, 2017 Dec 31, 2018 Mar 31, 2018 Jun 31, 2018 Sep 31, 2018 Dec 31
  // current date is 2017 Dec 31 and recurrence starts Jan 1
  //  -> 2016 Apr 1, 2016 Jul 1, 2016 Oct 1, 2017 Jan 1, 2017 Apr 1, 2017 Jul 1, 2017 Oct 1, *2018 Jan 1*
  const startRecurringDate = moment({ year: moment(currentDate).year() - 1, month: recurringMonth - 1, day: recurringDay });
  const quarterlyDates = [];
  for (let i = 1; i <= 8; i++) {
    quarterlyDates.push(startRecurringDate.clone().add(3 * i, 'months').endOf('day').toDate());
  }

  // determine the first that is greater than the current date
  for (let i = 0; i < quarterlyDates.length; i++) {
    if (currentDate <= quarterlyDates[i]) {
      return moment(quarterlyDates[i]);
    }
  }

  throw new Error(`Could not determine next quarterly date (recurring on M/D ${recurringMonth} / ${recurringDay}) from ${currentDate}`);
}

function getPreviousQuarterlyDate(currentDate, recurringMonth, recurringDay) {
  assertMonthAndDayAreValid(recurringMonth, recurringDay);

  const currentYear = moment(currentDate).year();

  if (recurringMonth === 2 && recurringDay === 29) {
    // quarterly recurring on Feb 29 will also recur on May/Aug/Nov,
    //   which are guaranteed to have a valid 29th day, so we just use May
    recurringMonth = 5; // eslint-disable-line no-param-reassign
  }

  const baseMoment = moment({ year: currentYear, month: recurringMonth - 1, day: recurringDay }).add(1, 'years');
  let potentialMoment = moment(baseMoment);
  let i = 0;
  while (potentialMoment.toDate() > currentDate) {
    i += 1;
    potentialMoment = moment(baseMoment).subtract(3 * i, 'months');
  }

  return moment(potentialMoment.toDate());
}

function getNextMonthlyDate(currentDate, recurringDay) {
  assertDayIsValid(recurringDay);

  const currentDay = moment(currentDate).date();

  if (currentDay <= recurringDay) {
    // return the same month
    const year = moment(currentDate).year();
    const month = moment(currentDate).month();
    const daysInMonth = getDaysInMonth(year, month + 1);

    return moment({ year, month, day: recurringDay <= daysInMonth ? recurringDay : daysInMonth });
  }

  // return the next month
  const year = moment(currentDate).add(1, 'month').year();
  const month = moment(currentDate).add(1, 'month').month();
  const daysInMonth = getDaysInMonth(year, month + 1);

  return moment({ year, month, day: recurringDay <= daysInMonth ? recurringDay : daysInMonth });
}

function getPreviousMonthlyDate(currentDate, recurringDay) {
  assertDayIsValid(recurringDay);

  const currentDay = moment(currentDate).date();

  if (currentDay > recurringDay) {
    // return the same month
    const year = moment(currentDate).year();
    const month = moment(currentDate).month();
    const daysInMonth = getDaysInMonth(year, month + 1);

    return moment({ year, month, day: recurringDay <= daysInMonth ? recurringDay : daysInMonth });
  }

  // return the previous month
  const year = moment(currentDate).subtract(1, 'month').year();
  const month = moment(currentDate).subtract(1, 'month').month();
  const daysInMonth = getDaysInMonth(year, month + 1);

  return moment({ year, month, day: recurringDay <= daysInMonth ? recurringDay : daysInMonth });
}

function getNextYearlyDate(currentDate, month, day) {
  assertMonthAndDayAreValid(month, day);

  const currentYear = moment(currentDate).year();
  const currentMonth = moment(currentDate).month() + 1; // NB: remember months are 0 indexed in js but not in our model
  const currentDay = moment(currentDate).date();

  if (currentMonth < month || (currentMonth === month && currentDay <= day)) {
    let adjustedDay = day;
    if (month === 2 && day === 29 && !moment({ year: currentYear }).isLeapYear()) {
      adjustedDay = 28;
    }

    // NB: remember months are 0 indexed in js
    return moment({ year: currentYear, month: month - 1, day: adjustedDay });
  }

  let adjustedDay = day;
  if (month === 2 && day === 29 && !moment({ year: currentYear + 1 }).isLeapYear()) {
    adjustedDay = 28;
  }

  // NB: remember months are 0 indexed in js but not in our model
  return moment({ year: currentYear + 1, month: month - 1, day: adjustedDay });
}

function getPreviousYearlyDate(currentDate, recurringMonth, recurringDay) {
  assertMonthAndDayAreValid(recurringMonth, recurringDay);

  const year = moment(currentDate).year();
  const currentMonth = moment(currentDate).month() + 1;
  const currentDay = moment(currentDate).date();

  if ((currentMonth > recurringMonth) || ((currentMonth === recurringMonth) && (currentDay > recurringDay))) {
    // return the same year
    const daysInMonth = getDaysInMonth(year, recurringMonth);

    return moment({ year, month: recurringMonth - 1, day: recurringDay <= daysInMonth ? recurringDay : daysInMonth });
  }

  // return the previous year
  const daysInMonth = getDaysInMonth(year - 1, recurringMonth);

  return moment({ year: year - 1, month: recurringMonth - 1, day: recurringDay <= daysInMonth ? recurringDay : daysInMonth });
}

function getEndOfDay(baseDate, timeZoneIdentifier) {
  const utcDate = moment.tz({ year: baseDate.getFullYear(), month: baseDate.getMonth(), day: baseDate.getDate() }, 'UTC');
  const minutesOffset = moment.tz.zone(timeZoneIdentifier).utcOffset(utcDate);
  const eod = utcDate.endOf('day');
  const eodAdj = eod.add(minutesOffset, 'minutes');
  const eodInTZ = eodAdj.tz(timeZoneIdentifier);
  return eodInTZ.toDate();
}

function getNextDueDate(interval, month, day, timeZoneIdentifier) {
  const currentDate = moment().toDate();
  let nextDate;

  if (!interval) {
    throw new Error('The date recurrence interval must be provided.');
  }

  switch (interval.toLowerCase().trim()) {
    case 'year':
      nextDate = getNextYearlyDate(currentDate, month, day);
      break;
    case 'quarter':
      nextDate = getNextQuarterlyDate(currentDate, month, day);
      break;
    case 'month':
      nextDate = getNextMonthlyDate(currentDate, day);
      break;
    default:
      throw new Error(`${interval} is not a valid interval.  Only year, month, and quarter intervals are currently supported`);
  }

  return getEndOfDay(nextDate.toDate(), timeZoneIdentifier || moment.tz.guess());
}

function getPreviousDueDate(interval, month, day, timeZoneIdentifier) {
  const currentDate = moment().toDate();
  let previousMoment;

  if (!interval) {
    throw new Error('The date recurrence interval must be provided.');
  }

  switch (interval.toLowerCase().trim()) {
    case 'year':
      previousMoment = getPreviousYearlyDate(currentDate, month, day);
      break;
    case 'quarter':
      previousMoment = getPreviousQuarterlyDate(currentDate, month, day);
      break;
    case 'month':
      previousMoment = getPreviousMonthlyDate(currentDate, day);
      break;
    default:
      throw new Error(`${interval} is not a valid interval.  Only year, month, and quarter intervals are currently supported`);
  }

  return getEndOfDay(previousMoment.toDate(), timeZoneIdentifier || moment.tz.guess());
}

module.exports = {
  getDaysInMonth,
  getMaxDaysInMonth,
  getNextQuarterlyDate,
  getPreviousQuarterlyDate,
  getNextMonthlyDate,
  getPreviousMonthlyDate,
  getNextYearlyDate,
  getPreviousYearlyDate,
  getEndOfDay,
  getNextDueDate,
  getPreviousDueDate,
  moment,
};
